import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { GridPinnedColumnFields } from "@mui/x-data-grid";
import FormDialog from "components/Dialog/modal-popup";
import { ReductionMeasureListModel } from "object-models/ReductionMeasures";
import React, { useEffect } from "react";
import CreateUpdateReductionMeasures from "../CreateUpdateReductionMeasures";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import { useLocation } from "react-router-dom";
import { TitleDefinitionConstants, UserRoles } from "constants/constant";
import { useAppState } from "context/appState.context";
import NzcDataGrid from "components/NzcDataGrid/NzcDataGrid";
import GuidanceBanner from "components/GuidanceBanner/GuidanceBanner";

interface ListReductionMeasuresDesignProps {
  reductionMeasureList: Array<ReductionMeasureListModel>;
  columns: any;
  openEditModal: boolean;
  selectedReductionMeasureId: number;
  customColumnMenu?: React.JSX.Element;
  onAddMeasureClick: () => void;
  handleCloseSnackBar: (state: AlertsProps) => void;
  hideDialogHandler: () => void;
  isRefreshEditClose: () => void;
  onUploadMeasureClick: () => void;
  onDownloadTemplateClick: () => void;
  isDownloadingTemplate: boolean;
  disableDownloadTemplate: boolean;
  showDashboardYearConstraint: boolean;
}

/**
 * ListReductionMeasuresDesign component displays a list of reduction measures in a DataGrid and handles interactions for adding and editing measures.
 *
 * @param {ListReductionMeasuresDesignProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */

export default function ListReductionMeasuresDesign(
  props: Readonly<ListReductionMeasuresDesignProps>,
) {
  const location = useLocation();
  const { userRole } = useAppState();

  const defaultPinnedColumn: GridPinnedColumnFields = {
    right: ["actions"],
  };

  useEffect(() => {
    if (location.state?.triggerAddMeasure) {
      props.onAddMeasureClick();
      localStorage.setItem("HighlightsMenu", "Reduction Measures");
    }
  }, [location.state, props]);

  return (
    <Box
      sx={{
        width: "calc(100vw - 320px)",
        maxWidth: "calc(100vw - 320px)",
        overflow: "visible",
        position: "relative",
      }}
    >
      <Box sx={{ minHeight: "100vh" }}>
        <React.Fragment>
          {props.showDashboardYearConstraint && (
            <GuidanceBanner
              sx={{ m: 2 }}
              title="Dashboard Year Constraint"
              message="If Growth Rate ends before the measure end year, the dashboard will only display up to the Growth Rate year."
            />
          )}
          <Grid container spacing={0} sx={{ mt: 0, width: "100%", maxWidth: "100%" }}>
            <Grid item xs={12} md={7}>
              <Typography
                fontSize="14px"
                fontFamily={"Inter"}
                fontWeight={700}
                data-testid="reductionMeasures"
              >
                Measure Details
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              {userRole !== UserRoles.Viewers && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    mt: { xs: 2, md: 0 },
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    alignItems="stretch"
                    sx={{
                      width: { xs: "100%", md: "auto" },
                      p: 0,
                      "& .MuiButton-root": {
                        minHeight: 30,
                        height: 30,
                        px: 2.25,
                        py: 0.75,
                        letterSpacing: "0.02em",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <Button
                      variant="contained"
                      data-testid="Upload-Measure-Button"
                      onClick={props.onUploadMeasureClick}
                      sx={{
                        fontSize: "10px",
                        fontFamily: "Inter",
                        fontWeight: 200,
                        minWidth: { xs: "100%", sm: 100 },
                      }}
                    >
                      Upload Measure
                    </Button>
                    <Button
                      variant="contained"
                      data-testid="Download-ReductionMeasures-Button"
                      onClick={props.onDownloadTemplateClick}
                      disabled={
                        props.disableDownloadTemplate ||
                        props.isDownloadingTemplate
                      }
                      startIcon={
                        props.isDownloadingTemplate ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                      sx={{
                        fontSize: "10px",
                        fontFamily: "Inter",
                        fontWeight: 200,
                        minWidth: { xs: "100%", sm: 220 },
                      }}
                    >
                      {props.isDownloadingTemplate
                        ? "Downloading..."
                        : "Download Reduction Measures"}
                    </Button>
                    <Button
                      variant="contained"
                      data-testid="AddNew-ReductionMeasure-Button"
                      onClick={props.onAddMeasureClick}
                      sx={{
                        fontSize: "10px",
                        fontFamily: "Inter",
                        fontWeight: 200,
                        minWidth: { xs: "100%", sm: 100 },
                      }}
                    >
                      Add Measure
                    </Button>
                  </Stack>
                </Box>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Card
              sx={{
                mt: 2,
                mb: 0,
                overflow: "hidden",
                width: "calc((100vw - 320px) / 0.85)",
                maxWidth: "calc((100vw - 320px) / 0.85)",
              }}
            >
              {props.reductionMeasureList.length > 0 ? (
                <Grid
                  container
                  sx={{ mt: 0, mb: 0, width: "100%", maxWidth: "100%" }}
                >
                  <Grid
                    item
                    xs={12}
                    sx={{
                      overflow: "auto",
                      transform: "scale(0.85)",
                      transformOrigin: "top left",
                      width: "calc((100vw - 320px) / 0.85)",
                      minHeight: "100vh",
                    }}
                  >
                    <NzcDataGrid
                      rows={props.reductionMeasureList}
                      columns={props.columns}
                      sortModel={[{ field: "reductionMeasureId", sort: "asc" }]}
                      pageSizeOptions={[5, 10]}
                      disableRowSelectionOnClick={true}
                      getRowId={(row) => row?.reductionMeasureId}
                      autoHeight
                      checkBoxSelection={false}
                      enableFiltering={true}
                      quickFilter={true}
                      columnVisibility={{
                        id: false,
                      }}
                      sx={{
                        width: "calc((100vw - 320px) / 0.85)",
                        minWidth: 0,
                      }}
                      userRoleParam={UserRoles.Viewers}
                      pinnedColumns={defaultPinnedColumn}
                      hidePagination={false}
                      pagination={true}
                    />
                  </Grid>
                </Grid>
              ) : null}
            </Card>
            {props.reductionMeasureList.length <= 0 ? (
              <Grid item xs={11} style={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  id="test-typography-01"
                  component="div"
                  color="gray"
                  style={{ fontWeight: "bold" }}
                  sx={{ mt: 15, mb: 20, ml: 20 }}
                >
                  {TitleDefinitionConstants.redutionMeasurenoRowsLabel}
                </Typography>
              </Grid>
            ) : null}
          </Grid>
          <FormDialog
            data-testid="alert-dialog-create"
            dialogOpen={props.openEditModal}
            hideDialogHandler={props.hideDialogHandler}
            dialogTitle="Edit Measure Information"
            maxWidth="lg"
            isFullScreenAllowed={true}
            childComponent={
              <CreateUpdateReductionMeasures
                isEdit={true}
                selectedReductionMeasureId={props.selectedReductionMeasureId}
                closeDialog={props.hideDialogHandler}
                handleCloseSnackBar={props.handleCloseSnackBar}
                isRefreshEditClose={props.isRefreshEditClose}
              ></CreateUpdateReductionMeasures>
            }
          ></FormDialog>
        </React.Fragment>
      </Box>
    </Box>
  );
}
